import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Courses = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [cart, setCart] = useState([]);
  const sessionId = sessionStorage.getItem('session_id') || Math.random().toString(36).substring(7);

  useEffect(() => {
    sessionStorage.setItem('session_id', sessionId);
    fetchCourses();
    fetchCart();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const addToCart = async (courseId) => {
    try {
      await axios.post(`${API}/cart`, { session_id: sessionId, course_id: courseId });
      toast.success('Added to cart!');
      fetchCart();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('Course already in cart');
      } else {
        toast.error('Failed to add to cart');
      }
    }
  };

  const isInCart = (courseId) => cart.some(item => item.course_id === courseId);

  const buyNow = (courseId) => {
    addToCart(courseId);
    setTimeout(() => navigate('/checkout'), 500);
  };

  return (
    <div className="courses-page">
      {/* Header */}
      <section className="py-20 bg-gradient-to-br from-[#f5f3f8] via-[#f5f3f8] to-[#f5f3f8]">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-800 mb-6" data-testid="courses-header">Professional Courses</h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Transform your teaching practice with our comprehensive, research-backed courses in early childhood education.
          </p>
        </div>
      </section>

      {/* Courses List */}
      <section className="py-20 bg-white" data-testid="courses-list">
        <div className="container mx-auto px-6">
          <div className="space-y-16">
            {courses.map((course, index) => (
              <div key={course.id} className="bg-gradient-to-br from-white to-[#f5f3f8] rounded-3xl shadow-xl overflow-hidden" data-testid={`course-item-${index}`}>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="relative h-[400px]">
                    <img 
                      src={course.image_url} 
                      alt={course.title} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 right-4 bg-[#a855f7] text-white px-6 py-2 rounded-full text-xl font-bold shadow-lg">
                      ₹{course.price}
                    </div>
                  </div>
                  <div className="p-8 space-y-6">
                    <h2 className="text-3xl lg:text-4xl font-bold text-gray-800">{course.title}</h2>
                    <p className="text-gray-700 leading-relaxed">{course.description}</p>
                    
                    {/* Modules Accordion */}
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800 mb-4">Course Modules</h3>
                      <Accordion type="single" collapsible className="space-y-2">
                        {course.modules.map((module, moduleIndex) => (
                          <AccordionItem key={moduleIndex} value={`module-${moduleIndex}`} className="border border-gray-200 rounded-lg px-4">
                            <AccordionTrigger className="text-lg font-semibold bg-gradient-to-r from-teal-500 via-blue-500 to-purple-500 bg-clip-text text-transparent hover:from-teal-600 hover:via-blue-600 hover:to-purple-600">
                              {module.title}
                            </AccordionTrigger>
                            <AccordionContent>
                              <ul className="space-y-2 mt-2">
                                {module.lessons.map((lesson, lessonIndex) => (
                                  <li key={lessonIndex} className="flex items-start gap-2 text-gray-600">
                                    <Check className="w-5 h-5 text-[#14b8a6] flex-shrink-0 mt-0.5" />
                                    <span>{lesson}</span>
                                  </li>
                                ))}
                              </ul>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4 pt-4">
                      <Button 
                        data-testid={`add-to-cart-btn-${index}`}
                        onClick={() => addToCart(course.id)}
                        disabled={isInCart(course.id)}
                        className="flex-1 bg-gradient-to-r from-teal-600 to-purple-600 hover:from-teal-700 hover:to-purple-700 text-white py-6 text-lg rounded-full disabled:opacity-50"
                      >
                        {isInCart(course.id) ? 'In Cart' : 'Add to Cart'}
                        <ShoppingCart className="ml-2" />
                      </Button>
                      <Button 
                        data-testid={`buy-now-btn-${index}`}
                        onClick={() => buyNow(course.id)}
                        className="flex-1 bg-gradient-to-r from-teal-600 to-purple-600 hover:from-teal-700 hover:to-purple-700 text-white py-6 text-lg rounded-full"
                      >
                        Buy Now
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Courses;